import { COMMAND_LABELS, type ChargerCommandBody } from '@pulsepoint/shared';
import { ApiError } from '../../api/client';
import { useSendCommand, type Charger, type CommandResult } from '../../api/chargers';
import { useToast } from '../../components/Toast';
import { chargerName } from './status';

/** What the charger's answer means, in words. */
export function describeResult(r: Pick<CommandResult, 'status' | 'response' | 'errorCode' | 'errorDescription'>): { ok: boolean; text: string } {
  const answer = r.response?.status;
  switch (r.status) {
    case 'accepted':
      return { ok: true, text: answer && answer !== 'Accepted' ? `The charger answered ${answer}.` : 'The charger accepted.' };
    case 'rejected':
      return { ok: false, text: `The charger refused${answer ? ` (${answer})` : ''}.` };
    case 'timeout':
      return { ok: false, text: 'No answer from the charger. It may be busy or have lost its connection.' };
    case 'error':
      return { ok: false, text: `The charger reported a problem: ${r.errorDescription ?? r.errorCode ?? 'unknown'}.` };
    default:
      return { ok: true, text: 'Sent. Waiting for the charger to answer.' };
  }
}

/** Sends a command to a charger and shows how it went. Resolves with the result (or null). */
export function useCommand(charger: Charger | undefined) {
  const send = useSendCommand();
  const toast = useToast();
  const run = async (body: ChargerCommandBody): Promise<CommandResult | null> => {
    if (!charger) return null;
    try {
      const r = await send.mutateAsync({ id: charger.id, ...body });
      const d = describeResult(r);
      toast(`${COMMAND_LABELS[body.action]}, ${chargerName(charger)}: ${d.text}`, d.ok ? 'success' : 'error');
      return r;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
      return null;
    }
  };
  return { run, busy: send.isPending, action: send.isPending ? send.variables?.action : undefined };
}
